/**
 * EventRuntime — executes event chains in the Event Graph.
 *
 * Execution model:
 *   fireEvent(type, data) finds all event nodes of that type and follows
 *   execOut → execIn edges, running each node's action in order.
 *   Delay nodes break the synchronous chain and resume via setTimeout.
 */
import { CustomAssetManager } from './CustomAssetManager.js'
import { danglingRefsMessage } from './prefabAssetRefs.js'
import { printDisplayText } from './printDisplayText.js'
import { resolveAudioAssetUrl } from './resolveAudioAssetUrl.js'
import { eventGraphState, subscribeEventGraph } from './eventGraphState'
import { debugState } from './debugState'
import { sentenceController } from './SentenceController'
import { setVariable, variableValues, getConstant } from './animVariables'
import { tracePhase } from './perfProbe'
import { devLog } from './devLog'
import { voxyManager } from './VoxyManager'
import { movement } from './movementComponent'
import { cameraController } from './cameraController'
import { headTrackedCamera } from './headTrackedCamera'
import { startersFor, starterMatches } from './eventStarters'
import { collectGroupStarters, findNodeOfType as findNodeOfTypeInGraph } from './graphGroup'
import { customEventStartersFor } from './customEvents'
import { input } from './inputComponent'
import { getBodyClipLoader } from './bodyClipLoaderRef'
import { playGeneratedGesture } from './generatedGestureClip.js'
import { buildBodyMotionClip, computeRootDelta, playGeneratedClip } from './bodyMotionClip.js'
import { stopBodyGesture } from './GestureChannel.js'
import { resolveGestureRoute } from './gestureRoute.js'
import { valuesEqual, compareOrder, toBoolean } from './compareValues.js'
import { MATH_OPS, computeMath } from './mathOps.js'
import { applyRendezvous } from './rendezvousState.js'
import { applyIndexedRendezvous } from './rendezvousIndexedState.js'
import { faceClipPlayer } from './FaceClipPlayer.js'
import { STREAM_FIRST_FRAME_CLIP, STREAM_LAST_FRAME_CLIP } from './sentenceFrameClips.js'
import { enqueueGesture } from './gestureSequencer.js'
import { validateTravelOnNavmesh } from './gesturePolicy.js'
import { createConstructGate } from './onConstructDispatch.js'
import { liveTuneStore } from './liveTuneStore'
import { applyEnvironment, unloadAuthoredEnvironment } from './applyEnvironment'
import {
  resolveEnvironmentSelection,
  environmentRowLabel,
  environmentRowUrl,
  isAlreadyActive,
  describeEnvironmentError,
  normalizeEnvironmentFormat,
  resolveEnvironmentFormatUrl,
} from './loadEnvironmentNode.js'
import { environmentFiles } from './environmentFiles.js'

// ── loadEnvironment (ENG-1031) ───────────────────────────────────────────────
// How long to wait for the org environment list before calling a `loadEnvironment`
// node's selection unresolvable. onConstruct fires as soon as the graphs are
// hydrated AND the avatar has loaded, which can be BEFORE CustomAssetManager's
// asset lists land — so an empty catalogue at fire time is "not yet", not "none".
// The wait ends on the first asset notification that carries environments.
const ENV_LIST_WAIT_MS = 15000
let _cancelEnvWait = null

function _applyEnvironmentSelection(sel, { allowWait = true } = {}) {
  const rows = CustomAssetManager.listEnvironmentAssets()
  const res = resolveEnvironmentSelection(rows, sel)
  // "None" is a deliberate choice — the node says load nothing. Drop whatever a
  // blueprint loaded before (a re-fired onConstruct after the selection was
  // changed to none, which is how a character stops booting into an
  // environment); unloadAuthoredEnvironment refuses to touch a user's own pick,
  // so the panel still wins. Not a warning: nothing is wrong.
  if (res.none) {
    if (unloadAuthoredEnvironment()) devLog('[Blueprint] loadEnvironment: none — unloaded the blueprint environment')
    return
  }
  if (res.error) {
    // Only an empty catalogue can be a timing artifact. A selection that
    // matches nothing in a populated list is a real, final failure.
    if (res.error === 'no-environments' && allowWait) {
      _waitForEnvironmentList(sel)
      return
    }
    console.warn(`[Blueprint] loadEnvironment: ${describeEnvironmentError(res.error, res.detail)}`)
    return
  }
  const fmt = normalizeEnvironmentFormat(sel?.format)
  if (!fmt) {
    // Default: the row's own current splat, verbatim — the synchronous path.
    _applyResolvedEnvironment(res.row, null)
    return
  }
  // A pinned format (ENG-1070): load a specific stored encoding (.ply vs .spz).
  // The URL is read VERBATIM from the environment's server-published files map
  // (environmentFiles) — never derived by rewriting the splat's extension. A
  // format the server does not list is a loud refusal, never a fallback to the
  // current splat.
  const rowUrl = environmentRowUrl(res.row)
  environmentFiles(rowUrl)
    .then((files) => {
      const r = resolveEnvironmentFormatUrl(res.row, fmt, files)
      if (r.error) {
        console.warn(`[Blueprint] loadEnvironment "${environmentRowLabel(res.row)}": ${describeEnvironmentError(r.error, r.detail)}`)
        return
      }
      _applyResolvedEnvironment(res.row, r.url)
    })
    .catch((err) => {
      console.warn(`[Blueprint] loadEnvironment "${environmentRowLabel(res.row)}": could not resolve the ${fmt.toUpperCase()} variant — ${err?.message ?? err}`)
    })
}

// Apply a resolved environment row, optionally overriding its splat URL with a
// format-keyed variant. Shared by the default (sync) and format (async) paths so
// the already-active guard and the blueprint-source semantics stay identical.
function _applyResolvedEnvironment(row, urlOverride) {
  // onConstruct re-fires on every applied editor edit; re-applying the same
  // splat would re-run the pose + shared-tune apply for no change. Compare the
  // URL we are ABOUT to load (a format override, when set), not the row's own.
  const targetUrl = urlOverride ?? environmentRowUrl(row)
  const active = liveTuneStore.getState()?.environment?.splatUrl ?? null
  if (isAlreadyActive({ url: targetUrl }, active)) {
    devLog(`[Blueprint] loadEnvironment "${environmentRowLabel(row)}" — already active, skipped`)
    return
  }
  // source 'blueprint': the graph asked, not the user — so this does not persist
  // into aos-livetune-v3 and cannot survive the node being removed
  // (tuneAuthored.js).
  const url = applyEnvironment(row, null, { source: 'blueprint', urlOverride })
  // applyEnvironment already warned about what it refused (no URL / dangling).
  if (url) devLog(`[Blueprint] loadEnvironment "${environmentRowLabel(row)}" → ${url}`)
}

function _waitForEnvironmentList(sel) {
  if (_cancelEnvWait) _cancelEnvWait()  // one pending wait; the newest ask wins
  let settled = false
  const finish = () => {
    if (settled) return
    settled = true
    unsub()
    clearTimeout(timer)
    if (_cancelEnvWait === finish) _cancelEnvWait = null
  }
  const unsub = CustomAssetManager.subscribe(() => {
    if (settled) return
    if (CustomAssetManager.listEnvironmentAssets().length === 0) return
    finish()
    _applyEnvironmentSelection(sel, { allowWait: false })
  })
  const timer = setTimeout(() => {
    finish()
    console.warn(`[Blueprint] loadEnvironment: ${describeEnvironmentError('no-environments')}`)
  }, ENV_LIST_WAIT_MS)
  _cancelEnvWait = finish
  devLog('[Blueprint] loadEnvironment: environment list not in yet — waiting for it')
}

const _messages = []  // { id, text, color, expiresAt }
let _msgId = 0

// Pure value-node operator sets (Comparison / Logical). The operator is the
// node type; _resolveDataPin dispatches on these. See src/lib/compareValues.js.
const COMPARISON_OPS = new Set(['equal', 'notEqual', 'greater', 'less', 'greaterEqual', 'lessEqual'])
const LOGICAL_OPS    = new Set(['and', 'or', 'not'])
// Math operator sets (+ - * /) live in ./mathOps.js so the pure arithmetic is
// unit-testable without importing the runtime; MATH_OPS is imported above.

// Rendezvous (AND-join) node arrival memory: { a, b } per node id. Runtime-only
// (NOT node.data → never serialized to localStorage, so a reload starts clean),
// lazily created on first arrival, auto-reset on success. Keyed by node id so
// two rendezvous nodes never collide. Exposed as window.__RENDEZVOUS_STATE__
// for the node's live A/B indicator + tests.
const _rendezvousState = new Map()

// Indexed Rendezvous arrival memory: { ready:number[], fired:number[],
// pending:number|null } per node id. Same runtime-only, node-local, re-armed-on-
// reset lifecycle as _rendezvousState; the transition is the pure
// applyIndexedRendezvous. Exposed as window.__RENDEZVOUS_INDEXED_STATE__ for the
// node's live indicator + tests.
const _rendezvousIndexedState = new Map()

// Bumped by stopSpeech. All pending setTimeout callbacks capture this value
// at creation time and abort if it has changed by the time they fire — this
// prevents a delayed prefab chain from resuming after an interrupt.
let _execEpoch = 0

const _lastEventData = {}  // { [type]: payload }

// URL → duration (seconds), populated by playAudio on loadedmetadata. Read
// back by _resolveDataPin('out-duration', …) so a Delay wired to playAudio
// can time itself against the real file length.
const _audioDurations = new Map()

// nodeId → last transcript string, populated by parlaySTT once its response
// returns. Downstream nodes read it via _resolveDataPin('out-transcript').
const _parlayTranscripts = new Map()

// Function call stack: each entry is { functionId, inputValues, callerNodes, callerEdges, callerNodeId }
const _callStack = []

// Last return values per function-call node id, so downstream data pins can read them.
const _fnReturnValues = {}  // { [callerNodeId]: { [pinName]: value } }

let _sendTextInput = null  // set by App.jsx via EventRuntime.setSendTextInput(fn)
let _applyConvorcherSession = null  // set by App.jsx via setApplyConvorcherSession(fn)

export const EventRuntime = {
  /** Current live print messages — poll this from UI. */
  get messages() {
    return _messages
  },

  /**
   * Fire all event nodes of the given type in the current event graph.
   * @param {'beginPlay'|string} type
   * @param {object} data  Optional payload stored for node display
   */
  fireEvent(type, data = {}) {
    tracePhase(`ev.${type}`, () => {
      _lastEventData[type] = data
      debugState.eventData[type] = data
      // Per-type fire counter — the e2e observable for "the construct chain
      // fired exactly once post-boot / again after an edit" (Wave 2B). Not a
      // hot path: events fire on user/server/graph actions, never per frame.
      debugState.eventFireCounts[type] = (debugState.eventFireCounts[type] ?? 0) + 1
      debugState.tick++

      const { nodes, edges } = eventGraphState
      const starters = startersFor(type, nodes)
      // One level into graphGroup containers — pool-passing, not flatten-splice (see
      // src/lib/graphGroup.js). Cached on the top-level nodes array identity, same as
      // `starters` above, so this costs nothing extra on the onPointerDrag/onWheel hot path.
      const groupStarters = collectGroupStarters(type, nodes)
      const totalMatches = starters.length + groupStarters.length
      if (type === 'onTextInput') {
        devLog(`[EventRuntime] fireEvent("${type}") — payload:`, data, `| matching event nodes in graph: ${totalMatches}`)
        if (totalMatches === 0) {
          console.warn(`[EventRuntime] No "onTextInput" event node found in the Event Graph. Text won't be sent. Add an onTextInput event → Send Text Input node.`)
        }
      }
      // Iterate + skip rather than filter: a bound-node predicate must not
      // allocate a second array per fire (onPointerDrag fires every frame).
      for (const node of starters) {
        if (!starterMatches(type, node, data)) continue
        _runFrom(node.id, nodes, edges)
      }
      // Grouped starters run against their OWN group's pool, never the top-level one — two
      // groups minted from the same template can carry colliding inner ids and must not
      // cross-wire (see graphGroup.js doc comment).
      for (const { node, groupNode } of groupStarters) {
        if (!starterMatches(type, node, data)) continue
        _runFrom(node.id, groupNode.data?.innerNodes ?? [], groupNode.data?.innerEdges ?? [])
      }
    })
  },

  /**
   * Fire a NAMED custom event (ENG-1062). Unlike fireEvent (matches starter
   * nodes by `n.type === type`), this matches `customEvent` nodes by their
   * `data.name`, so a state-machine transition's Actions can trigger an
   * event-graph chain the author wired up. Runs every matching starter (top
   * level + one level into any graphGroup), fanning out like fireEvent.
   *
   * Not a hot path — fired only when a transition is actually taken, never per
   * frame — so the per-name filter allocation here is fine (the by-type list it
   * filters is still the cached one from startersFor).
   * @param {string} name  the custom event's name
   * @param {object} data  optional payload (e.g. { smNodeId, from, to })
   */
  fireCustomEvent(name, data = {}) {
    const key = `customEvent:${name}`
    tracePhase(`ev.${key}`, () => {
      _lastEventData[key] = data
      debugState.eventData[key] = data
      debugState.eventFireCounts[key] = (debugState.eventFireCounts[key] ?? 0) + 1
      debugState.tick++

      const { nodes, edges } = eventGraphState
      const starters = customEventStartersFor(name, startersFor('customEvent', nodes))
      const groupStarters = collectGroupStarters('customEvent', nodes)
      const want = String(name ?? '').trim()
      for (const node of starters) {
        _runFrom(node.id, nodes, edges)
      }
      for (const { node, groupNode } of groupStarters) {
        if (String(node?.data?.name ?? '').trim() !== want || !want) continue
        _runFrom(node.id, groupNode.data?.innerNodes ?? [], groupNode.data?.innerEdges ?? [])
      }
    })
  },

  setSendTextInput(fn) { _sendTextInput = fn },

  /** Set the callback that applies a convorcher session config (character +
   *  features + story) from a `convorcherSession` node. Wired in App.jsx. */
  setApplyConvorcherSession(fn) { _applyConvorcherSession = fn },

  /**
   * True when the top-level graph — or one level into any `graphGroup`
   * container's `data.innerNodes` — contains a node of `type`. GLOBAL_UNIQUE_TYPES
   * (EventGraph.jsx) allows exactly one instance of types like `voxyConfig` /
   * `convorcherSession` and explicitly permits it to live inside a group, so any
   * consumer that gates runtime behavior on "does this node exist" must check both
   * pools — mirrors the one-level recursion in graphGroup.js / cameraRigCompat.js.
   * Not a hot path (poll/subscription consumers only), so the extra loop is fine.
   */
  hasNodeOfType(type) {
    return !!findNodeOfTypeInGraph(type, eventGraphState.nodes)
  },

  /** Same two-pool scan as hasNodeOfType (top-level nodes, plus one level into
   *  any graphGroup's innerNodes), but returns the node object itself (or
   *  null) instead of a boolean — for declarative config nodes (e.g.
   *  `headTrackedCamera`) whose `data` a sync effect needs to read, not just
   *  detect presence of. Both methods delegate to the same pure scan
   *  (graphGroup.js's findNodeOfType) so there is exactly one copy of this
   *  loop, unit-tested there (tests/unit/graphGroup.test.mjs) since
   *  EventRuntime.js itself can't be imported under a plain node test run —
   *  see that module's doc comment. */
  findNodeOfType(type) {
    return findNodeOfTypeInGraph(type, eventGraphState.nodes)
  },

  /** True when the current event graph owns the convorcher session via a
   *  `convorcherSession` node — App.jsx then defers its auto-setCharacter. */
  hasConvorcherSessionNode() {
    return this.hasNodeOfType('convorcherSession')
  },

  /**
   * @deprecated Thin alias for fireEvent('inputKey', { key }). Kept while App.jsx
   * still owns the keydown listener; inputComponent takes that over at the camera
   * cutover, at which point this goes.
   *
   * This used to be a PARALLEL execution path that filtered + walked the graph
   * itself, bypassing fireEvent entirely. That's why `inputKey` nodes could never
   * have data pins: the parallel path never wrote _lastEventData/debugState.eventData,
   * so _resolveDataPin's `out-<field>` fallback had nothing to resolve against. It
   * also skipped tracePhase, making key-driven chains invisible to the perf probe.
   * Routing through fireEvent (with the `inputKey` starter predicate doing the
   * key-matching this method used to do inline) fixes all three at once.
   */
  fireInputKey(key) {
    this.fireEvent('inputKey', { key })
  },

  /** Return the last payload fired for a given event type. */
  getLastData(type) {
    return _lastEventData[type] ?? null
  },

  /**
   * Play a prefab by its tag, outside of an event-graph exec chain.
   * Used by the Prefab Editor "Test" button. The `playPrefab` graph node
   * shares the same lookup + playback machinery via _doPlayPrefab.
   * Returns the playback duration in seconds, or null if the tag is unknown.
   */
  playPrefabByTag(tag) {
    const all = CustomAssetManager.listPrefabs()
    devLog(`[playPrefab manual] tag="${tag}" available=[${all.map(p => p.tag).join(', ') || '(none)'}]`)
    const prefab = all.find(p => p.tag === tag)
    if (!prefab) {
      console.warn(`[playPrefab manual] no prefab with tag "${tag}"`)
      return null
    }
    // Direct mode so the prefab shows even when the blueprint has no prefabSlot
    // wired into the Final Animation.
    _ensurePrefabResident(prefab)
      .then(() => _doPlayPrefab(prefab, { direct: true }))
      .catch(err => console.warn(`[playPrefab manual] ensure-load failed for "${tag}":`, err))
    return (prefab.duration_ms ?? 5000) / 1000
  },

  /**
   * Conversation-triggered prefab playback (ENG-834). Called from App.jsx
   * when a Convorcher NewResponse trigger resolves to a DB-backed prefab row.
   *
   * Preserves the legacy blueprint chain's conversation semantics (the old
   * onPrefabTrigger → concat → playBodyClip path in conversational.json):
   *   - ConversationalState is set to "prefab" while the body clip plays and
   *     reset to "idle" afterwards, so the body/face state machines transition
   *     exactly as before. Set only when a body clip exists — for audio/face-
   *     only prefabs the SM's passthrough "prefab" state would zero the body
   *     weights (frozen avatar), so those play over the current state instead.
   *   - The reset waits for max(body clip, audio) duration — the audio drives
   *     the conversational moment, mirroring the old playAudio → delay wiring.
   *   - Barge-in parity: every timer captures _execEpoch. The blueprint's
   *     stopSpeech node (fired by the onBargeIn chain) bumps the epoch, pauses
   *     __PREFAB_AUDIO__ and clears __DIRECT_PLAY__, which aborts this path
   *     exactly like it aborted the legacy chain. The barge-in chain then owns
   *     ConversationalState (sets "listening"), so the reset must not fire.
   * On top of that it gains Path-B safety: residency gating (no T-pose while
   * clips stream in) and full-strength face playback (weight 1.0, matching the
   * legacy playFaceClip node — NOT the editor Test button's 0.8 preview).
   * Returns the expected playback duration in seconds.
   */
  playPrefabConversational(prefab) {
    const capturedEpoch = _execEpoch
    const { bodyClipName } = CustomAssetManager.resolvePrefabClipNames(prefab)
    const hasBody = !!bodyClipName
    // Always-on (see handleNewResponse in App.jsx): one line per fired prefab.
    console.log(`[playPrefab conversational] tag="${prefab.tag}" hasBody=${hasBody} dur=${prefab.duration_ms ?? '?'}ms`)

    _ensurePrefabResident(prefab)
      .then(() => {
        if (_execEpoch !== capturedEpoch) return  // barge-in while clips were loading
        // Enter the SM's prefab state only once playback actually starts —
        // setting it before residency would park the SM in the passthrough
        // state with no __DIRECT_PLAY__ yet (bind pose for the load window).
        if (hasBody) setVariable('ConversationalState', 'prefab')
        const duration = _doPlayPrefab(prefab, { direct: true, faceWeight: 1.0 })
        if (!hasBody) return  // nothing to reset — state was never entered

        // Reset to idle after the moment ends. Baseline is the longer of the
        // body clip and the authored duration_ms (backfilled = measured audio
        // length); if the audio element reports a longer real duration once
        // its metadata lands, extend the timer — this is the delay-node parity.
        const reset = () => {
          if (_execEpoch !== capturedEpoch) return  // interrupted — barge-in owns state now
          if (variableValues.ConversationalState === 'prefab') {
            setVariable('ConversationalState', 'idle')
          }
        }
        let resetMs = Math.max(duration * 1000, prefab.duration_ms ?? 0)
        let timer = setTimeout(reset, resetMs)
        const audio = typeof window !== 'undefined' ? window.__PREFAB_AUDIO__ : null
        if (audio) {
          const extend = () => {
            if (!Number.isFinite(audio.duration)) return
            const audioMs = audio.duration * 1000
            if (audioMs > resetMs) {
              clearTimeout(timer)
              resetMs = audioMs
              timer = setTimeout(reset, audioMs)
            }
          }
          if (audio.readyState >= 1) extend()
          else audio.addEventListener('loadedmetadata', extend, { once: true })
        }
      })
      .catch(err => console.warn(`[playPrefab conversational] ensure-load failed for "${prefab.tag}":`, err))

    return (prefab.duration_ms ?? 5000) / 1000
  },

  /** Push a message to the viewport overlay (same system as the Print node). */
  printMessage(text, { color = '#ffff00', duration = 5 } = {}) {
    _messages.push({
      id: _msgId++,
      text: String(text),
      color,
      expiresAt: Date.now() + duration * 1000,
    })
  },

  /** Remove messages whose duration has elapsed. Call from a UI poll. */
  prune() {
    const now = Date.now()
    for (let i = _messages.length - 1; i >= 0; i--) {
      if (_messages[i].expiresAt <= now) _messages.splice(i, 1)
    }
  },
}

// Expose for e2e tests
if (typeof window !== 'undefined') {
  window.__EVENT_RUNTIME__ = EventRuntime
  window.__RENDEZVOUS_STATE__ = _rendezvousState
  window.__RENDEZVOUS_INDEXED_STATE__ = _rendezvousIndexedState
  // ENG-1062: fire a named custom event from the console / an e2e spec.
  window.__FIRE_CUSTOM_EVENT__ = (name, data = {}) => EventRuntime.fireCustomEvent(name, data)
}

// onConstruct (ENG-981 rework + Wave 2B "one node"): the Unreal
// construction-script analog — and, since beginPlay merged into it, also the
// play-start event. It fires:
//   1. once, when the graphs are hydrated AND the avatar has finished
//      loading (the old beginPlay guarantee — App.jsx opens the gate via
//      EventRuntime.setConstructReady(true)); and
//   2. on every eventGraphState notification after that — graph re-hydration
//      and every applied editor edit (BlueprintEditor.jsx notifies after
//      each) — so a headTrackedCamera / camera-rig pose / voxyConfig wired
//      from it re-applies live as you tune it, in the one merged mode.
// Notifications BEFORE the avatar loads are suppressed by the gate
// (onConstructDispatch.createConstructGate): chains that need the loaded
// avatar's runtime hooks (voxyConfig, convorcherSession, camera rig setup)
// must not fire early — the exact ordering beginPlay existed to protect. A
// character switch closes the gate (setConstructReady(false)), so the next
// graph waits for the next avatar; reopening fires the once-per-load
// construct again.
//
// The gate wraps wrapDispatch for re-entrancy: if fireEvent('onConstruct')
// itself somehow triggered a nested graph-change notification, the nested
// call is a no-op instead of recursing — see onConstructDispatch.js's header
// for why this is a plain synchronous boolean rather than anything fancier.
//
// HMR guard (whole-branch review, Important #2): this module-scope
// subscribeEventGraph() call re-runs on every HMR re-execution of this file,
// and eventGraphState's `_subs` Set dedups by FUNCTION REFERENCE — a fresh
// closure every reload — so without this guard, each edit-save-reload cycle
// adds ANOTHER subscriber, and a later graph edit fires onConstruct once per
// reload survived. Harmless today only because syncFromGraph is idempotent;
// a future non-idempotent onConstruct consumer would silently misbehave.
// A plain module-scope variable would NOT survive this (it resets on the
// same re-execution that creates the problem); `window` is the one thing
// that survives a module's own HMR reload, and CLAUDE.md's `window.__*`
// debug-global convention already establishes this repo's comfort with
// hanging singleton/live state off it. Stash the unsubscribe fn there and
// call it before re-subscribing, so at most one subscription is ever live
// no matter how many times this module re-executes. A real page load starts
// with a fresh `window`, so this is a no-op there — the guard only matters
// across HMR/dev-server re-execution, never across separate test runs.
if (typeof window !== 'undefined' && typeof window.__ON_CONSTRUCT_UNSUB__ === 'function') {
  window.__ON_CONSTRUCT_UNSUB__()
}
// The gate's { ready } state lives on window for the same HMR reason as the
// unsubscribe fn: this module's re-execution builds a fresh gate, and losing
// `ready` there would silently stop edits from re-firing onConstruct until
// the next avatar load. A real page load starts with a fresh window → closed.
const _constructGateState = (typeof window !== 'undefined')
  ? (window.__CONSTRUCT_GATE_STATE__ ??= { ready: false })
  : { ready: false }
const _constructGate = createConstructGate(() => EventRuntime.fireEvent('onConstruct'), _constructGateState)
/** App.jsx calls this with `blueprintReady && loaded` — see the gate's doc. */
EventRuntime.setConstructReady = (ready) => _constructGate.setReady(ready)
const _onConstructUnsub = subscribeEventGraph(() => _constructGate.notify())
if (typeof window !== 'undefined') {
  window.__ON_CONSTRUCT_UNSUB__ = _onConstructUnsub
}

// `enteredHandle` is the target-pin id this exec chain arrived through (e.g.
// 'execInB'). Optional + additive: every existing node ignores it — only nodes
// with more than one exec input (Rendezvous) need to know which one fired.
function _runFrom(nodeId, nodes, edges, enteredHandle) {
  const node = nodes.find(n => n.id === nodeId)
  if (!node) return
  _runNode(node, nodes, edges, enteredHandle)
}

// Resolve a prefab's clips and wait until both are resident before playback.
// Prefab clips load asynchronously — CUSTOM clips download their glb/json,
// and STOCK clips stream in from the static manifest on demand (JIT). Firing
// before the resolved clip is resident points __DIRECT_PLAY__ at a missing
// action, so the avatar snaps to bind pose for the play window — the "1s
// T-pose, no motion" bug. Shared by playPrefabByTag (Test button) and
// playPrefabConversational (NewResponse trigger). Resolves immediately when
// everything is already loaded.
function _ensurePrefabResident(prefab) {
  const { bodyClipName, faceClipName } = CustomAssetManager.resolvePrefabClipNames(prefab)
  const actions = (typeof window !== 'undefined' && window.__AVATAR_ACTIONS__) || {}
  const facePlayer = typeof window !== 'undefined' ? window.__FACE_CLIP_PLAYER__ : null
  const bodyResident = !bodyClipName || (bodyClipName in actions)
  const faceResident = !faceClipName || !!facePlayer?._clips?.[faceClipName]
  if (bodyResident && faceResident) return Promise.resolve()

  const waits = []
  // Custom clips (body.glb / face.json) must be downloaded + registered.
  if (prefab.body_glb_url || prefab.face_json_url) waits.push(CustomAssetManager.ensurePrefabLoaded(prefab))
  // Stock body clip not yet resident — pull it to the front of the loader
  // queue and wait for registration via its promise API (built for exactly
  // this: one-shot triggers that need the action present before they act).
  if (bodyClipName && !prefab.body_glb_url && !(bodyClipName in actions)) {
    const p = getBodyClipLoader()?.ensure?.(bodyClipName)
    if (p) waits.push(p)
  }
  return Promise.all(waits)
}

// Play a prefab record's body + face + audio. Shared between the
// `playPrefab` graph node and EventRuntime.playPrefabByTag (used by the
// Prefab Editor "Test" button). Returns the playback duration in seconds.
//
// Logging is intentionally always-on here (not devLog) — prefab playback is
// a low-frequency user-driven action and the resolution chain
// (tag → prefab row → clip names → loaded actions) has enough moving parts
// that silent failure is hard to diagnose. The line count per call is small.
function _doPlayPrefab(prefab, { direct = false, faceWeight = 0.8 } = {}) {
  const { bodyClipName, faceClipName } = CustomAssetManager.resolvePrefabClipNames(prefab)
  // A reference to an asset the server cannot produce is a DEFECT IN THE DATA, and
  // it used to be indistinguishable from "this prefab has no body clip": the row
  // arrives with the url absent, bodyClipName resolves to null, and the prefab
  // plays its face and audio with nothing said. Reported once per playback,
  // loudly, naming the row and the ids (prefabAssetRefs.js).
  const dangling = danglingRefsMessage(prefab, 'prefab')
  if (dangling) console.error(`[playPrefab] ${dangling}`)
  const actions = typeof window !== 'undefined' ? window.__AVATAR_ACTIONS__ : null
  const action  = bodyClipName ? actions?.[bodyClipName] : null
  const facePlayer = typeof window !== 'undefined' ? window.__FACE_CLIP_PLAYER__ : null
  const faceLoaded = faceClipName ? !!facePlayer?._clips?.[faceClipName] : false
  const duration = action
    ? action.getClip().duration
    : (prefab.duration_ms ?? 5000) / 1000

  // Sources for resolution: server-joined URLs (snake_case mirrors) drive
  // custom-asset clip names; falls back to stock clip names when no URL is set.
  devLog(
    `[playPrefab${direct ? ' direct' : ''}] tag="${prefab.tag}" id=${prefab.id} status=${prefab.status}`,
    `\n  body: clip="${bodyClipName ?? '∅'}" loaded=${!!action} (glb=${prefab.body_glb_url ? 'yes' : '—'}, stock=${prefab.body_clip_name ?? '—'})`,
    `\n  face: clip="${faceClipName ?? '∅'}" loaded=${faceLoaded} (json=${prefab.face_json_url ? 'yes' : '—'}, stock=${prefab.face_clip_name ?? '—'})`,
    `\n  audio: url="${prefab.audio_url ?? '∅'}"`,
    `\n  dur=${duration.toFixed(2)}s`
  )

  if (typeof window !== 'undefined') {
    if (direct && bodyClipName) {
      // Direct mode (Prefab Editor "Test" button): bypass the body graph
      // entirely via __DIRECT_PLAY__ so the prefab is visible even when the
      // blueprint has no prefabSlot wired into the Final Animation. The flag
      // self-clears in GraphEvaluator once expiresAt passes.
      const now = Date.now()
      window.__DIRECT_PLAY__ = {
        clipName: bodyClipName,
        startTime: now,
        expiresAt: now + duration * 1000,
        // Snap in/out — no blend envelope on the Test path. Direct-play stops
        // the idle base, so any partial-weight ramp interpolates the clip
        // against the avatar's bind pose and flashes a T/A-pose for the ramp
        // duration. A crossfade would require keeping the outgoing idle at a
        // complementary weight; for a one-shot preview a hard cut is correct and
        // artifact-free. The live runtime path crossfades via the SM using the
        // prefab's authored blend_in/out values, so nothing is lost there.
        blendInMs: 0,
        blendOutMs: 0,
      }
    } else {
      // Graph-driven mode (playPrefab node): set the slot so prefabSlot
      // nodes in body/face graphs activate; the graph handles blending via
      // prefabSlot → Blend wiring.
      window.__PREFAB_ACTIVE__ = { clipName: bodyClipName, tag: prefab.tag, expiresAt: Date.now() + duration * 1000 }
      if (action) {
        action.setLoop(2200 /* THREE.LoopOnce */, 1)
        action.clampWhenFinished = true
        action.reset()
        action.play()
      } else if (bodyClipName) {
        console.warn(`[playPrefab] body clip "${bodyClipName}" not in __AVATAR_ACTIONS__ — was it loaded? (status=${prefab.status})`)
      }
    }
  }

  if (faceClipName && facePlayer) {
    if (faceLoaded) {
      facePlayer.manualPlay(faceClipName, faceWeight, duration * 1000)
    } else {
      console.warn(`[playPrefab] face clip "${faceClipName}" not loaded in FaceClipPlayer`)
    }
  }

  if (prefab.audio_url && typeof window !== 'undefined') {
    const a    = new Audio(prefab.audio_url)
    const prev = window.__PREFAB_AUDIO__
    if (prev && prev !== a) { try { prev.pause(); prev.src = '' } catch { /* ignore */ } }
    window.__PREFAB_AUDIO__ = a
    a.play().catch(err => console.warn(`[playPrefab] audio "${prefab.audio_url}" failed:`, err))
  }

  return duration
}

/**
 * Coerce a wired data value to a [x,y,z] vector, or null if it isn't one.
 * Vectors/rotators/transforms travel as plain JS arrays (or {x,y,z} objects)
 * on the untyped data wires — there's no formal vector pin type.
 */
function asVec3(v) {
  if (Array.isArray(v) && v.length >= 3) return [Number(v[0]), Number(v[1]), Number(v[2])]
  if (v && typeof v === 'object' && 'x' in v && 'z' in v) return [Number(v.x), Number(v.y ?? 0), Number(v.z)]
  return null
}

const RAD2DEG = 180 / Math.PI
const DEG2RAD = Math.PI / 180

// Read a value off "the character" (the avatar's root group) for the
// Character→Transform getter nodes. Forward/right/up derive from the yaw with
// the rig's +Z = rest-forward convention (matches movementComponent + headFollow).
// Returns null until the avatar mounts (movement.getGroup() is set).
function characterGet(kind) {
  const g = movement.getGroup()
  if (!g) return null
  const p = g.position, r = g.rotation, s = g.scale, yaw = r.y
  switch (kind) {
    case 'location': return [p.x, p.y, p.z]
    case 'rotation': return [r.x * RAD2DEG, r.y * RAD2DEG, r.z * RAD2DEG]
    case 'forward':  return [Math.sin(yaw), 0, Math.cos(yaw)]
    case 'right':    return [Math.cos(yaw), 0, -Math.sin(yaw)]
    case 'up':       return [0, 1, 0]
    case 'transform': return {
      location: [p.x, p.y, p.z],
      rotation: [r.x * RAD2DEG, r.y * RAD2DEG, r.z * RAD2DEG],
      scale: [s.x, s.y, s.z],
    }
    default: return null
  }
}

// Write a value onto the character group for the Set Character * nodes. Rotation
// is Euler degrees. Returns false if the avatar isn't mounted. (NB: position.y is
// normally owned by avatarFloor; setting it may be corrected next frame.)
function characterSet(kind, value) {
  const g = movement.getGroup()
  if (!g) return false
  if (kind === 'location') {
    const v = asVec3(value); if (!v) return false
    g.position.set(v[0], v[1], v[2])
  } else if (kind === 'rotation') {
    const v = asVec3(value); if (!v) return false
    g.rotation.set(v[0] * DEG2RAD, v[1] * DEG2RAD, v[2] * DEG2RAD)
  } else if (kind === 'transform') {
    if (!value || typeof value !== 'object') return false
    const l = asVec3(value.location), r = asVec3(value.rotation), s = asVec3(value.scale)
    if (l) g.position.set(l[0], l[1], l[2])
    if (r) g.rotation.set(r[0] * DEG2RAD, r[1] * DEG2RAD, r[2] * DEG2RAD)
    if (s) g.scale.set(s[0], s[1], s[2])
  }
  return true
}

/**
 * Resolve a data-pin input for a node.
 * Handles three source types:
 *   - Random node  → evaluates fresh random value
 *   - Event node   → reads last fired payload field (sourceHandle: "out-<field>")
 *   - (future)     → variable get nodes, etc.
 */
/**
 * Resolve a camera motion node's `damping` (orbit inertia), wired pin first.
 *
 * `in-damping` is a PIN, not just a data field, so the glide can be computed in-graph
 * rather than frozen at author time — e.g. more float when zoomed out, none during a
 * cinematic, more on a fast flick. That parity with `in-delta` is the point: a value the
 * graph can't compute isn't really authored.
 *
 * A wired non-finite value falls back to the static field rather than to 0. Silently
 * collapsing to "instant" would be a feel change with no signal — the loud-failure rule
 * applied to a number: prefer the author's last explicit intent over a fabricated default.
 * Range is left to dampFactor, which is the single authority (≤0 instant, clamped at 0.999).
 */
function _resolveDamping(node, fallback, nodes, edges) {
  const wired = _resolveDataPin('in-damping', node.id, nodes, edges)
  if (wired !== null) {
    const n = Number(wired)
    if (Number.isFinite(n)) return n
  }
  return Number(node.data?.damping ?? fallback)
}

function _resolveDataPin(targetHandle, nodeId, nodes, edges) {
  const edge = edges.find(e => e.target === nodeId && e.targetHandle === targetHandle)
  if (!edge) return null
  const srcNode = nodes.find(n => n.id === edge.source)
  if (!srcNode) return null

  if (srcNode.type === 'makeLiteralString') return srcNode.data.value ?? ''
  if (srcNode.type === 'makeLiteralFloat')  return srcNode.data.value ?? 0
  if (srcNode.type === 'makeLiteralInt')    return srcNode.data.value ?? 0

  // Function entry: read input value from the top call-stack frame
  if (srcNode.type === 'functionEntry') {
    const frame = _callStack[_callStack.length - 1]
    const pin = edge.sourceHandle?.replace(/^out-/, '')
    return frame?.inputValues?.[pin] ?? null
  }

  // Function call output: read return value stored when the function returned
  if (srcNode.type === 'functionCall') {
    const pin = edge.sourceHandle?.replace(/^out-/, '')
    return _fnReturnValues[srcNode.id]?.[pin] ?? null
  }

  if (srcNode.type === 'getVariable') {
    const name = srcNode.data?.variableName ?? srcNode.data?.varName
    return name ? (variableValues[name] ?? null) : null
  }

  // Character → Transform getters (data-output nodes; no exec). Emit a vector /
  // rotator (Euler degrees) / transform read off the avatar group.
  if (srcNode.type === 'getCharacterLocation')  return characterGet('location')
  if (srcNode.type === 'getCharacterRotation')  return characterGet('rotation')
  if (srcNode.type === 'getCharacterForward')   return characterGet('forward')
  if (srcNode.type === 'getCharacterRight')     return characterGet('right')
  if (srcNode.type === 'getCharacterUp')        return characterGet('up')
  if (srcNode.type === 'getCharacterTransform') return characterGet('transform')

  // Camera transform getter — { location, rotation, target, fov }.
  if (srcNode.type === 'getCameraTransform')    return cameraController.getTransform()

  // Input data reads. "Hold" is something the graph ASKS about — that's why these are data
  // nodes and not a repeating event: no key-repeat machinery, no per-frame re-fire.
  if (srcNode.type === 'isButtonHeld')          return input.isButtonHeld(srcNode.data?.button ?? 'any')
  if (srcNode.type === 'isKeyHeld')             return input.isKeyHeld(srcNode.data?.key ?? '')
  if (srcNode.type === 'getInputAxis')          return input.getAxis(srcNode.data?.axis ?? 'mouseX')

  if (srcNode.type === 'random') {
    const min = srcNode.data.min ?? 0
    const max = srcNode.data.max ?? 1
    return srcNode.data.randomType === 'int'
      ? Math.floor(Math.random() * (Math.floor(max) - Math.ceil(min) + 1)) + Math.ceil(min)
      : Math.random() * (max - min) + min
  }

  if (srcNode.type === 'playClip') return srcNode.data.clipName ?? ''

  if (srcNode.type === 'convorcherSession') {
    // `out-session_id` — resolved from the session_id constant set by App's
    // onSession handler. Null until the server's `session` message lands.
    if (edge.sourceHandle === 'out-session_id') return getConstant('session_id')
    return null
  }

  if (srcNode.type === 'prefabSlot') {
    const state  = typeof window !== 'undefined' ? window.__PREFAB_ACTIVE__ : null
    const active = !!(state?.clipName && Date.now() < state.expiresAt)
    const handle = edge.sourceHandle
    if (handle === 'tag')       return active ? (state.tag ?? state.clipName ?? '') : ''
    if (handle === 'is-active') return active ? 1 : 0
    return active ? 1 : 0
  }

  if (srcNode.type === 'select') {
    const idxResolved = _resolveDataPin('in-index', srcNode.id, nodes, edges)
    const index   = idxResolved !== null ? Math.round(Number(idxResolved)) : (srcNode.data.index ?? 0)
    const clamped = Math.max(0, Math.min((srcNode.data.optionCount ?? 2) - 1, index))
    return _resolveDataPin(`option-${clamped}`, srcNode.id, nodes, edges)
  }

  if (srcNode.type === 'parlaySTT') {
    // Latest transcript returned by this parlaySTT node's most recent run.
    // Null until the WS round-trip completes — downstream consumers running
    // on the same exec chain will only see this after advance().
    const t = _parlayTranscripts.get(srcNode.id)
    return t !== undefined ? t : null
  }

  if (srcNode.type === 'playAudio') {
    // `out-duration` returns the cached duration (seconds) of whatever URL
    // this playAudio node most-recently resolved. Zero if the audio hasn't
    // been loaded yet (downstream delay just fires instantly in that case).
    const wiredUrl = _resolveDataPin('in-url', srcNode.id, nodes, edges)
    const url      = wiredUrl !== null ? String(wiredUrl) : (srcNode.data?.url ?? '')
    return _audioDurations.get(url) ?? 0
  }

  // ── TArray data nodes ──
  if (srcNode.type === 'arrayGet') {
    const varName = srcNode.data?.varName
    const wiredIdx = _resolveDataPin('in-index', srcNode.id, nodes, edges)
    const index = wiredIdx !== null ? Math.round(Number(wiredIdx)) : (srcNode.data?.index ?? 0)
    const arr = variableValues[varName]
    if (!Array.isArray(arr)) return null
    const v = arr[index]
    return v !== undefined ? v : null
  }

  if (srcNode.type === 'arrayLength') {
    const varName = srcNode.data?.varName
    const arr = variableValues[varName]
    return Array.isArray(arr) ? arr.length : 0
  }

  // ── TMap data nodes ──
  if (srcNode.type === 'mapGet') {
    const varName = srcNode.data?.varName
    const wiredKey = _resolveDataPin('in-key', srcNode.id, nodes, edges)
    const rawKey = wiredKey !== null ? wiredKey : (srcNode.data?.key ?? '')
    const key = srcNode.data?.keyType === 'int' ? String(Math.round(Number(rawKey))) : String(rawKey)
    const map = variableValues[varName]
    if (!map || typeof map !== 'object' || Array.isArray(map)) return null
    const v = map[key]
    return v !== undefined ? v : null
  }

  if (srcNode.type === 'mapHasKey') {
    const varName = srcNode.data?.varName
    const wiredKey = _resolveDataPin('in-key', srcNode.id, nodes, edges)
    const rawKey = wiredKey !== null ? wiredKey : (srcNode.data?.key ?? '')
    const key = srcNode.data?.keyType === 'int' ? String(Math.round(Number(rawKey))) : String(rawKey)
    const map = variableValues[varName]
    if (!map || typeof map !== 'object' || Array.isArray(map)) return false
    return Object.prototype.hasOwnProperty.call(map, key)
  }

  if (srcNode.type === 'mapLength') {
    const varName = srcNode.data?.varName
    const map = variableValues[varName]
    if (!map || typeof map !== 'object' || Array.isArray(map)) return 0
    return Object.keys(map).length
  }

  if (srcNode.type === 'concatString') {
    const count = Math.max(2, srcNode.data?.count ?? 2)
    let out = ''
    for (let i = 0; i < count; i++) {
      const v = _resolveDataPin(`in-${i}`, srcNode.id, nodes, edges)
      if (v !== null && v !== undefined) out += String(v)
    }
    // Stash for the node's on-canvas preview
    srcNode.data = srcNode.data || {}
    srcNode.data._preview = out
    return out
  }

  // Comparison operators (== != > < >= <=) → bool. The operator IS the node
  // type. `==`/`!=` are type-aware (valuesEqual); ordering uses compareOrder.
  // Each operand is the wired pin, or the node's inline literal box (data.a /
  // data.b, a string) when unwired — wire wins.
  if (COMPARISON_OPS.has(srcNode.type)) {
    const wa = _resolveDataPin('in-a', srcNode.id, nodes, edges)
    const wb = _resolveDataPin('in-b', srcNode.id, nodes, edges)
    const a = wa !== null ? wa : (srcNode.data?.a ?? '')
    const b = wb !== null ? wb : (srcNode.data?.b ?? '')
    let result
    switch (srcNode.type) {
      case 'equal':        result = valuesEqual(a, b);       break
      case 'notEqual':     result = !valuesEqual(a, b);      break
      case 'greater':      result = compareOrder(a, b) > 0;  break
      case 'less':         result = compareOrder(a, b) < 0;  break
      case 'greaterEqual': result = compareOrder(a, b) >= 0; break
      case 'lessEqual':    result = compareOrder(a, b) <= 0; break
      default:             result = false
    }
    srcNode.data = srcNode.data || {}
    srcNode.data._preview = result
    return result
  }

  // Logical operators (&& || !) → bool. Each operand is the wired pin, or the
  // inline literal box when unwired, coerced via toBoolean. `not` is unary.
  if (LOGICAL_OPS.has(srcNode.type)) {
    const wa = _resolveDataPin('in-a', srcNode.id, nodes, edges)
    const a = toBoolean(wa !== null ? wa : (srcNode.data?.a ?? ''))
    let result
    if (srcNode.type === 'not') {
      result = !a
    } else {
      const wb = _resolveDataPin('in-b', srcNode.id, nodes, edges)
      const b = toBoolean(wb !== null ? wb : (srcNode.data?.b ?? ''))
      result = srcNode.type === 'and' ? (a && b) : (a || b)
    }
    srcNode.data = srcNode.data || {}
    srcNode.data._preview = result
    return result
  }

  // Math operators (+ - * /) → number. Each operand is the wired pin, or the
  // node's inline literal box (data.a / data.b, a string) when unwired — wire
  // wins. Operands coerce via toNumber (null/empty/non-numeric → 0). Per-node
  // int mode (data.resultType === 'int') truncates toward zero. Divide-by-zero
  // returns 0 and shows ÷0 in the preview — a NaN/Infinity never escapes here.
  // See src/lib/mathOps.js.
  if (MATH_OPS.has(srcNode.type)) {
    const wa = _resolveDataPin('in-a', srcNode.id, nodes, edges)
    const wb = _resolveDataPin('in-b', srcNode.id, nodes, edges)
    const a = wa !== null ? wa : (srcNode.data?.a ?? '')
    const b = wb !== null ? wb : (srcNode.data?.b ?? '')
    const { value, note } = computeMath(srcNode.type, a, b, srcNode.data?.resultType === 'int')
    srcNode.data = srcNode.data || {}
    srcNode.data._preview = note !== null ? note : value
    return value
  }

  const field = edge.sourceHandle?.replace(/^out-/, '')
  if (!field) return null
  const eventData = _lastEventData[srcNode.type]
  const value = eventData?.[field]
  return value !== undefined ? value : null
}

/**
 * Walk the exec chain up from `nodeId` and return the payload of the nearest
 * ancestor that is an event node (i.e. has a last-fired payload). Lets an
 * action node read the event data that started its chain. Nodes with no
 * payload (branch, delay, …) are skipped transparently.
 * @returns {any|null} the ancestor event payload, or null if none
 */
function _walkUpEventData(nodeId, nodes, edges) {
  let cur = nodeId
  const visited = new Set()
  while (cur && !visited.has(cur)) {
    visited.add(cur)
    const n = nodes.find(nd => nd.id === cur)
    if (n && _lastEventData[n.type] != null) return _lastEventData[n.type]
    const execEdge = edges.find(e => e.target === cur && e.targetHandle === 'execIn')
    cur = execEdge ? execEdge.source : null
  }
  return null
}

/** Server gesture tag → a stable registered clip name, e.g. "Wave" → kimodo_wave. */
function _kimodoClipName(animName) {
  const slug = String(animName ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return slug ? `kimodo_${slug}` : 'kimodo_gesture'
}

function _runNode(node, nodes, edges, enteredHandle) {
  // Record execution timestamp for visual pulse + log
  debugState.nodeLastFired[node.id] = Date.now()
  debugState.tick++
  devLog(`[Blueprint] ▶ ${node.type}  id=${node.id}`)

  if (node.type === 'delay') {
    const wiredDuration = _resolveDataPin('in-duration', node.id, nodes, edges)
    const ms = ((wiredDuration !== null ? wiredDuration : node.data?.duration) ?? 1) * 1000
    const next = edges.find(e => e.source === node.id && (e.sourceHandle === 'execOut' || e.sourceHandle == null))
    const capturedEpoch = _execEpoch
    if (next) setTimeout(() => {
      if (_execEpoch !== capturedEpoch) return  // chain was interrupted by stopSpeech
      _runFrom(next.target, nodes, edges, next.targetHandle)
    }, ms)
    return  // break sync chain — resume after timeout
  }

  if (node.type === 'branch') {
    const condition = _resolveDataPin('in-condition', node.id, nodes, edges)
    const handleId = condition ? 'execTrue' : 'execFalse'
    devLog(`[Blueprint] Branch condition=${condition} → ${handleId}`)
    const next = edges.find(e => e.source === node.id && e.sourceHandle === handleId)
    if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
    return
  }

  if (node.type === 'rendezvous') {
    // AND-join: fire execOut only once BOTH inputs (execInA + execInB) have
    // arrived. Memory is node-local (module Map keyed by node id) and the
    // transition is the pure applyRendezvous — booleans per input (idempotent to
    // a repeated arrival), auto-reset on success so it re-arms. `execReset`
    // clears a half-completed join without firing. Which input fired comes from
    // `enteredHandle` (the runtime change that made this node possible).
    const input = enteredHandle === 'execReset' ? 'reset'
                : enteredHandle === 'execInA'   ? 'A'
                : enteredHandle === 'execInB'   ? 'B'
                : null
    if (input === null) return  // entered via an unknown pin — nothing to record
    const res = applyRendezvous(_rendezvousState.get(node.id), input)
    _rendezvousState.set(node.id, { a: res.a, b: res.b })
    debugState.tick++  // nudge the editor poll so the node's A/B indicator updates
    devLog(`[Blueprint] rendezvous ${node.id} ${input} → a=${res.a} b=${res.b} fire=${res.fire}`)
    if (!res.fire) return  // still waiting on the other side
    const next = edges.find(e => e.source === node.id && e.sourceHandle === 'execOut')
    if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
    return
  }

  if (node.type === 'rendezvousIndexed') {
    // Indexed AND-join: B(index) marks an index ready; A(index) fires execOut
    // once B has marked the SAME index ready, in either arrival order — an exact
    // match, no arithmetic (increment the A-index upstream if you want n → n+1).
    // `execReset` clears the cycle (wire it to onNewResponse so a new response's
    // re-numbered indices start fresh). Each side's index comes off its wired int
    // pin; the pure transition is applyIndexedRendezvous. Which input fired is
    // `enteredHandle`, same as the plain rendezvous.
    const input = enteredHandle === 'execReset' ? 'reset'
                : enteredHandle === 'execInA'   ? 'A'
                : enteredHandle === 'execInB'   ? 'B'
                : null
    if (input === null) return  // entered via an unknown pin — nothing to record
    let index = NaN
    if (input === 'A' || input === 'B') {
      const wired = _resolveDataPin(input === 'A' ? 'in-indexA' : 'in-indexB', node.id, nodes, edges)
      index = wired !== null ? Number(wired) : NaN
    }
    const res = applyIndexedRendezvous(_rendezvousIndexedState.get(node.id), input, index)
    _rendezvousIndexedState.set(node.id, { ready: res.ready, fired: res.fired, pending: res.pending })
    debugState.tick++  // nudge the editor poll so the node's live indicator updates
    devLog(`[Blueprint] rendezvousIndexed ${node.id} ${input}(${index}) → pending=${res.pending} fire=${res.fire}${res.fire ? ` idx=${res.index}` : ''}`)
    if (!res.fire) return
    const next = edges.find(e => e.source === node.id && e.sourceHandle === 'execOut')
    if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
    return
  }

  if (node.type === 'playFirstSentence')   sentenceController.playFirst()
  if (node.type === 'playNextSentence')    sentenceController.playNext()
  // Stream First/Last Frame: publish a queued sentence's first (or last) ARKit
  // frame as a static 1-frame face clip, so the face-graph First/Last Frame Pose
  // node can blend into it. Index: the wired `in-index` pin (a sentenceNum) when
  // present, else the default — 'first' = next sentence to play, 'last' =
  // current sentence (see SentenceController.getStreamFrame). No-ops (leaves the
  // clip unchanged) when that sentence's frames aren't ready yet.
  if (node.type === 'streamFirstFrame' || node.type === 'streamLastFrame') {
    const which = node.type === 'streamFirstFrame' ? 'first' : 'last'
    const wired = _resolveDataPin('in-index', node.id, nodes, edges)
    const sentenceNum = wired !== null && Number.isFinite(Number(wired)) ? Number(wired) : null
    const frame = sentenceController.getStreamFrame(which, sentenceNum)
    if (frame) {
      const clipName = which === 'first' ? STREAM_FIRST_FRAME_CLIP : STREAM_LAST_FRAME_CLIP
      faceClipPlayer.loadCustomClip(clipName, { frames: [{ timeCode: 0, blendshapeWeights: frame }] })
      devLog(`[Blueprint] ${node.type} → published ${clipName} (n=${sentenceNum ?? 'default'})`)
    } else {
      devLog(`[Blueprint] ${node.type} — no frames for sentence ${sentenceNum ?? 'default'}, clip unchanged`)
    }
  }
  if (node.type === 'stopSpeech') {
    // Bump epoch first — cancels all pending delay/playAudio/playBodyClip
    // setTimeout callbacks that are still in-flight from a prior prefab chain.
    _execEpoch++
    try { sentenceController.reset() } catch (e) { console.warn('[Blueprint] stopSpeech error:', e) }
    if (typeof window !== 'undefined') {
      try { window.__PREFAB_AUDIO__?.pause?.() } catch { /* ignore */ }
      window.__PREFAB_AUDIO__ = null
      // Cancel any direct-play body clip and face clip overrides so prefab
      // animations stop immediately instead of running to their natural end.
      window.__DIRECT_PLAY__ = null
      window.__PREFAB_ACTIVE__ = null
      try { window.__FACE_CLIP_PLAYER__?.cancelAllOverrides?.() } catch { /* ignore */ }
    }
  }

  // ── Debug nodes ──
  if (node.type === 'playFaceClip') {
    const wiredName = _resolveDataPin('in-name', node.id, nodes, edges)
    const clipName  = wiredName !== null ? String(wiredName) : (node.data?.clipName ?? '')
    const wiredDur  = _resolveDataPin('in-duration', node.id, nodes, edges)
    const durationMs = wiredDur !== null ? Number(wiredDur) * 1000 : (node.data?.durationMs ?? 5000)
    const player = typeof window !== 'undefined' ? window.__FACE_CLIP_PLAYER__ : null
    if (player && clipName) {
      player.manualPlay(clipName, 1.0, durationMs)
      devLog(`[Blueprint] playFaceClip: ${clipName} (${(durationMs/1000).toFixed(1)}s)`)
    }
  }
  if (node.type === 'listClips') {
    const actions = typeof window !== 'undefined' ? window.__AVATAR_ACTIONS__ : null
    const player = typeof window !== 'undefined' ? window.__FACE_CLIP_PLAYER__ : null
    devLog('[Blueprint] Body clips:', actions ? Object.keys(actions) : '(mixer not ready)')
    devLog('[Blueprint] Face clips:', player ? Object.keys(player._clips) : '(player not ready)')
  }
  if (node.type === 'logState') {
    const w = typeof window !== 'undefined' ? window : null
    devLog('[Blueprint] State dump:', {
      variables: w?.__VARIABLE_VALUES__,
      bodySM:    w?.__SM_RUNTIME__?.sm_body,
      faceSM:    w?.__SM_RUNTIME__?.sm_face,
      weights:   w?.__DEBUG_STATE__?.clipWeights,
    })
  }

  if (node.type === 'playGesture') {
    const wiredName = _resolveDataPin('in-name', node.id, nodes, edges)
    const name = wiredName !== null ? String(wiredName) : (node.data?.name ?? '')
    const player = typeof window !== 'undefined' ? window.__FACE_CLIP_PLAYER__ : null
    // The ancestor event payload (onGesture) carries `tracks` when convorcher
    // served a kimodo-GENERATED gesture inline on TextOut. Same walk-up the
    // print node uses — the payload, not a window global.
    const eventData = _walkUpEventData(node.id, nodes, edges)
    const tracks = eventData?.tracks
    if (!tracks) {
      // Stock gestures.json tag — no generated clip, nothing to sequence. The
      // mapping's own face/body durations apply; unchanged pre-sequencer path.
      if (player && name) player.playGesture(name)
    } else {
      // Generated clip: convorcher can attach SEVERAL animations to one sentence,
      // each firing its own onGesture. The playback channels are single-slot, so
      // play them IN ORDER through the sequencer — the first starts synchronously
      // in this same tick (byte-identical to the old single-gesture behavior),
      // the rest queue behind it by clip duration.
      const clipName = _kimodoClipName(eventData?.animName || name)
      const motionClass = eventData?.motionClass
      // Clip length from the payload + the blend tail, so consecutive gestures
      // don't stomp each other's fade-out. Falls back to the sequencer default.
      const fps = tracks.fps || 30
      const durationMs = tracks.frames > 0 ? (tracks.frames / fps) * 1000 + 300 : 0
      enqueueGesture({ durationMs, start: () => {
        if (player && name) {
          // Generated body clip drives the body → suppress the mapping's stock body
          // clip so the two don't fight over GestureChannel's single slot. Face
          // fires inside the thunk so it stays in sync with its body clip. Face
          // first: a not-ready throw from the body must not cost us the face.
          player.playGesture(name, { body: false })
        }
        // motion_class routes a generated clip to one of two channels:
        // 'performance' (QC-classified
        // travelling/full-body motion — a dance can't play in place, additively,
        // over the SM base) takes the SAME override channel BodyMotion
        // locomotion already uses. Anything else — absent, 'gesture', an
        // old-server payload — is byte-identical to the pre-existing additive
        // path.
        const route = resolveGestureRoute(tracks, motionClass)
        if (route === 'performance') {
          // Namespaced apart from `clipName`: the additive path (playGeneratedGesture,
          // below) registers under `clipName` too. Mixed-class keys are expected (the
          // POOL ENTRY is classified, not the key), so a same-key gesture-class serve
          // arriving mid-performance would otherwise re-register that mixer action out
          // from under the live __DIRECT_PLAY__ override — two actions driving the same
          // bones, and if net travel is under the commit threshold, the old action is
          // never stopped and is orphaned playing forever.
          const perfClipName = `${clipName}_perf`
          // Play-time spatial policy: with a navmesh loaded, a performance whose
          // root path leaves walkable space is REJECTED outright — no rescale,
          // no clamp (see gesturePolicy.js). Checked inside the sequencer thunk
          // deliberately: the avatar's position NOW (after any prior queued
          // clip's root commit) is what the travel is relative to. No navmesh →
          // admissible, unchanged behavior.
          // The thunk only ever runs in-browser (setTimeout/performance.now), so
          // read window directly — consistent with the __AVATAR_GROUP__ deref below.
          const nm = window.__NAVMESH_INSTANCE__
          const verdict = validateTravelOnNavmesh(tracks, window.__AVATAR_GROUP__?.position, nm)
          if (!verdict.ok) {
            console.warn(`[Blueprint] playGesture: performance "${perfClipName}" rejected by navmesh policy: ${verdict.reason}`)
            return
          }
          try {
            // Kill any in-flight additive envelope now rather than letting it
            // ride out its own fade — applyWeights already zeroes its weight
            // while __DIRECT_PLAY__ is active, but leaving the channel "active"
            // would let it pop back in for the tail of its envelope once the
            // performance's override hold releases.
            stopBodyGesture()
            const clip = buildBodyMotionClip(tracks, { name: perfClipName, rootMode: 'travel' })
            playGeneratedClip(clip, { commitRootDelta: computeRootDelta(tracks), blendInMs: 250 })
            devLog(`[Blueprint] playGesture: performance clip "${perfClipName}" (face="${name}")`)
          } catch (e) {
            // playGeneratedClip throws by design when the avatar isn't ready.
            // Loud, but a blueprint node must never break the exec chain.
            console.warn(`[Blueprint] playGesture: performance clip "${perfClipName}" failed:`, e)
          }
        } else {
          try {
            playGeneratedGesture(tracks, { name: clipName })
            devLog(`[Blueprint] playGesture: generated body clip "${clipName}" (face="${name}")`)
          } catch (e) {
            // playGeneratedGesture throws by design when the avatar isn't ready.
            // Loud, but a blueprint node must never break the exec chain.
            console.warn(`[Blueprint] playGesture: generated gesture "${clipName}" failed:`, e)
          }
        }
      } })
    }
  }

  // ── Wayfinding / movement (ENG-592) ──
  // Fire-and-forget: these issue a move to the movementComponent (which paths
  // over the navmesh and walks the avatar across frames) and let the exec chain
  // continue immediately. Completion is reported via the onMoveCompleted event.
  if (node.type === 'loadEnvironment') {
    const wired = _resolveDataPin('in-environment', node.id, nodes, edges)
    _applyEnvironmentSelection({
      environmentId: node.data?.environmentId,
      environmentName: node.data?.environmentName,
      format: node.data?.format,
      wired: wired !== null ? String(wired) : '',
    })
  }

  if (node.type === 'moveToLocation') {
    // Prefer a wired vector on in-location; fall back to legacy per-axis pins,
    // then the manual X/Y/Z fields.
    const wiredLoc = _resolveDataPin('in-location', node.id, nodes, edges)
    const rx = _resolveDataPin('in-x', node.id, nodes, edges)
    const ry = _resolveDataPin('in-y', node.id, nodes, edges)
    const rz = _resolveDataPin('in-z', node.id, nodes, edges)
    const loc = asVec3(wiredLoc) || [
      rx !== null ? Number(rx) : Number(node.data?.x ?? 0),
      ry !== null ? Number(ry) : Number(node.data?.y ?? 0),
      rz !== null ? Number(rz) : Number(node.data?.z ?? 0),
    ]
    const acceptanceRadius = Number(node.data?.acceptanceRadius ?? 0.2)
    movement.moveToLocation(loc, { acceptanceRadius })
    devLog(`[Blueprint] moveToLocation ${JSON.stringify(loc)}`)
  }

  if (node.type === 'moveToActor') {
    const wired = _resolveDataPin('in-target', node.id, nodes, edges)
    const target = wired !== null ? wired : (node.data?.target ?? '')
    const acceptanceRadius = Number(node.data?.acceptanceRadius ?? 0.2)
    if (target === '' || target == null) {
      console.warn('[Blueprint] moveToActor: empty target')
    } else {
      movement.moveToActor(target, { acceptanceRadius })
      devLog(`[Blueprint] moveToActor "${target}"`)
    }
  }

  if (node.type === 'moveToRandomPoint') {
    const rr = _resolveDataPin('in-radius', node.id, nodes, edges)
    const radius = rr !== null ? Number(rr) : Number(node.data?.radius ?? 3)
    // Prefer a wired vector on in-center; otherwise the manual X/Y/Z fields.
    const wiredCenter = _resolveDataPin('in-center', node.id, nodes, edges)
    const center = asVec3(wiredCenter)
      || [Number(node.data?.centerX ?? 0), Number(node.data?.centerY ?? 0), Number(node.data?.centerZ ?? 0)]
    movement.moveToRandomPoint(center, radius)
    devLog(`[Blueprint] moveToRandomPoint center=${JSON.stringify(center)} r=${radius}`)
  }

  if (node.type === 'abortMovement') {
    movement.abort()
    devLog('[Blueprint] abortMovement')
  }

  // ── Character → Transform setters (exec actions) ──
  if (node.type === 'setCharacterLocation') {
    const wired = _resolveDataPin('in-location', node.id, nodes, edges)
    const loc = asVec3(wired) || [Number(node.data?.x ?? 0), Number(node.data?.y ?? 0), Number(node.data?.z ?? 0)]
    characterSet('location', loc)
    devLog(`[Blueprint] setCharacterLocation ${JSON.stringify(loc)}`)
  }

  if (node.type === 'setCharacterRotation') {
    const wired = _resolveDataPin('in-rotation', node.id, nodes, edges)
    const rot = asVec3(wired) || [Number(node.data?.rx ?? 0), Number(node.data?.ry ?? 0), Number(node.data?.rz ?? 0)]
    characterSet('rotation', rot)
    devLog(`[Blueprint] setCharacterRotation ${JSON.stringify(rot)}°`)
  }

  if (node.type === 'setCharacterTransform') {
    const t = _resolveDataPin('in-transform', node.id, nodes, edges)
    if (t) { characterSet('transform', t); devLog('[Blueprint] setCharacterTransform') }
    else console.warn('[Blueprint] setCharacterTransform: in-transform not wired')
  }

  // ── Camera rig motion (camera-in-blueprint) ──
  // These do NOT go through liveTuneStore: rig pose is transient state the graph rewrites
  // every frame from input, and the store's precedence machinery is per-key + per-event, so
  // "panel-locked" has no meaning here. Hence no applied/ignored devLog either — that
  // contract belongs to the CONFIG setters below. Panel-lock governs config, never motion.
  //
  // devLog is deliberately absent from the motion handlers: they run every frame during a
  // drag, and logging there would flood the console and allocate a string per frame.
  if (node.type === 'cameraAddYaw' || node.type === 'cameraAddPitch' || node.type === 'cameraAddDolly') {
    const w = _resolveDataPin('in-delta', node.id, nodes, edges)
    const raw = w !== null ? Number(w) : Number(node.data?.delta ?? 0)
    const sensDefault = node.type === 'cameraAddDolly' ? 0.001 : 0.005
    const sens = Number(node.data?.sensitivity ?? sensDefault)
    const d = (node.data?.invert ? -raw : raw) * sens
    // Momentum retention per frame-at-60fps. 0.95 restores the inertia drei's OrbitControls
    // gave us by default (enableDamping true + dampingFactor 0.05); the controller's own API
    // default is 0 (instant), so this node default is what makes the INTERACTIVE camera glide.
    //
    // Dolly defaults to 0 because that IS parity: neither OrbitControls variant ever damped
    // zoom — three-stdlib applies spherical.radius * scale immediately and resets scale = 1
    // every update, outside the enableDamping branch (three-stdlib/controls/OrbitControls.js:225,286).
    // Damping a discrete wheel notch would smear it over a second: a feel CHANGE, not a
    // restoration. Authors on trackpads can still opt in by setting the field.
    const dampDefault = node.type === 'cameraAddDolly' ? 0 : 0.95
    const damp = _resolveDamping(node, dampDefault, nodes, edges)
    if (node.type === 'cameraAddYaw') cameraController.addYaw(d, damp)
    else if (node.type === 'cameraAddPitch') cameraController.addPitch(d, damp)
    else cameraController.addDolly(d, damp)
  }

  if (node.type === 'cameraPanTarget') {
    const wx = _resolveDataPin('in-dx', node.id, nodes, edges)
    const wy = _resolveDataPin('in-dy', node.id, nodes, edges)
    const dx = wx !== null ? Number(wx) : Number(node.data?.dx ?? 0)
    const dy = wy !== null ? Number(wy) : Number(node.data?.dy ?? 0)
    cameraController.panTarget(
      dx, dy,
      Number(node.data?.sensitivity ?? 0.0015),
      _resolveDamping(node, 0.95, nodes, edges),  // three damped panOffset alongside rotate
    )
  }

  if (node.type === 'cameraSetRigLimits') {
    // maxDist 0 means "unbounded" in the UI — a literal 0 would trap the camera at the target.
    const maxDist = Number(node.data?.maxDist ?? 0)
    cameraController.setRigLimits({
      minDist: Number(node.data?.minDist ?? 0.6),
      maxDist: maxDist > 0 ? maxDist : Infinity,
      minPitch: Number(node.data?.minPitch ?? -89) * DEG2RAD,
      maxPitch: Number(node.data?.maxPitch ?? 89) * DEG2RAD,
    })
    devLog(`[Blueprint] cameraSetRigLimits dist=[${node.data?.minDist ?? 0.6}, ${maxDist > 0 ? maxDist : '∞'}] pitch=[${node.data?.minPitch ?? -89}°, ${node.data?.maxPitch ?? 89}°]`)
  }

  if (node.type === 'cameraSetRigPose') {
    const w = _resolveDataPin('in-target', node.id, nodes, edges)
    const t = asVec3(w) || [Number(node.data?.x ?? 0), Number(node.data?.y ?? 1.35), Number(node.data?.z ?? 0)]
    const b = _resolveDataPin('in-blend', node.id, nodes, edges)
    const blendMs = b !== null ? Number(b) : Number(node.data?.blendMs ?? 0)
    cameraController.setRigPose({
      yaw: Number(node.data?.yaw ?? 0) * DEG2RAD,
      pitch: Number(node.data?.pitch ?? 10) * DEG2RAD,
      dist: Number(node.data?.dist ?? 3),
      target: t,
    }, blendMs)
    devLog(`[Blueprint] cameraSetRigPose yaw=${node.data?.yaw ?? 0}° pitch=${node.data?.pitch ?? 10}° dist=${node.data?.dist ?? 3} blend=${blendMs}ms`)
  }

  // ── Camera config (camera-in-blueprint) ──
  // Setters route through cameraController → liveTuneStore.setCameraFromBlueprint,
  // so a LiveTunePanel value the dev touched this session still wins.
  if (node.type === 'setCameraFov') {
    const w = _resolveDataPin('in-fov', node.id, nodes, edges)
    const fov = w !== null ? Number(w) : Number(node.data?.fov ?? 45)
    const b = _resolveDataPin('in-blend', node.id, nodes, edges)
    const blendMs = b !== null ? Number(b) : Number(node.data?.blendMs ?? 0)
    const applied = cameraController.setFov(fov, blendMs)
    devLog(`[Blueprint] setCameraFov ${fov} blend=${blendMs}ms — ${applied ? 'applied' : 'ignored (panel-locked)'}`)
  }

  if (node.type === 'setCameraTarget') {
    const mode = node.data?.mode ?? 'vector'
    const b = _resolveDataPin('in-blend', node.id, nodes, edges)
    const blendMs = b !== null ? Number(b) : Number(node.data?.blendMs ?? 0)
    let applied
    if (mode === 'head') {
      applied = cameraController.setTarget({ mode: 'head' }, blendMs)
    } else {
      const w = _resolveDataPin('in-target', node.id, nodes, edges)
      const v = asVec3(w) || [Number(node.data?.x ?? 0), Number(node.data?.y ?? 0), Number(node.data?.z ?? 0)]
      applied = cameraController.setTarget({ mode: 'vector', value: v }, blendMs)
    }
    devLog(`[Blueprint] setCameraTarget ${mode} blend=${blendMs}ms — ${applied ? 'applied' : 'ignored (panel-locked)'}`)
  }

  if (node.type === 'setCameraCage') {
    const w = _resolveDataPin('in-halfExtents', node.id, nodes, edges)
    const he = asVec3(w) || [Number(node.data?.hx ?? 2.5), Number(node.data?.hy ?? 1.25), Number(node.data?.hz ?? 2.5)]
    const applied = cameraController.setCage({
      enabled: node.data?.enabled !== false,
      hx: he[0], hy: he[1], hz: he[2],
      margin: Number(node.data?.margin ?? 0.5),
    })
    devLog(`[Blueprint] setCameraCage enabled=${node.data?.enabled !== false} — ${applied ? 'applied' : 'ignored (panel-locked)'}`)
  }

  if (node.type === 'setCameraSeeThrough') {
    const applied = cameraController.setSeeThrough({
      enabled: !!node.data?.enabled,
      radius: Number(node.data?.radius ?? 0.6),
      fade: Number(node.data?.fade ?? 0.3),
      auto: !!node.data?.auto,
      autoNear: Number(node.data?.autoNear ?? 1.2),
      autoBelow: node.data?.autoBelow !== false,
    })
    devLog(`[Blueprint] setCameraSeeThrough enabled=${!!node.data?.enabled} auto=${!!node.data?.auto} — ${applied ? 'applied' : 'ignored (panel-locked)'}`)
  }

  if (node.type === 'setCameraFollow') {
    const w = _resolveDataPin('in-offset', node.id, nodes, edges)
    const off = asVec3(w) || [Number(node.data?.x ?? 0), Number(node.data?.y ?? 1.6), Number(node.data?.z ?? 3)]
    const applied = cameraController.setFollow({
      enabled: !!node.data?.enabled,
      offset: off,
      space: node.data?.space === 'world' ? 'world' : 'local',
      damping: Number(node.data?.damping ?? 0.12),
      targetOffsetY: Number(node.data?.targetOffsetY ?? 0),
    })
    devLog(`[Blueprint] setCameraFollow enabled=${!!node.data?.enabled} space=${node.data?.space ?? 'local'} — ${applied ? 'applied' : 'ignored (panel-locked)'}`)
  }

  // The backdrop half of a saved Scene Look (see sceneLookNodes.js). The camera
  // setters above route through cameraController → setCameraFromBlueprint; this
  // one goes to setStateAuthored, which is the right sibling for a slice with no
  // per-key `_touched` tracking: the write is marked blueprint-authored, so
  // tuneAuthored strips it out of localStorage and it can never masquerade as
  // the dev's own setting next load — the exact failure the `loadEnvironment`
  // node shipped with. A later user edit clears the mark and takes ownership.
  if (node.type === 'setBackdrop') {
    const d = node.data ?? {}
    const patch = {
      skyEnabled: !!d.skyEnabled,
      groundEnabled: !!d.groundEnabled,
      hideEnvironment: !!d.hideEnvironment,
    }
    // Colours and size only when the node states them: a node authored before a
    // field existed must inherit the default, not overwrite it with undefined.
    if (d.skyColor) patch.skyColor = d.skyColor
    if (d.groundColor) patch.groundColor = d.groundColor
    if (Number.isFinite(Number(d.groundSize))) patch.groundSize = Number(d.groundSize)
    liveTuneStore.setStateAuthored({ backdrop: patch }, { source: 'blueprint' })
    devLog(`[Blueprint] setBackdrop sky=${patch.skyEnabled ? patch.skyColor ?? 'on' : 'off'} `
      + `ground=${patch.groundEnabled ? patch.groundColor ?? 'on' : 'off'}`)
  }

  if (node.type === 'restorePreviousState') {
    const prev = variableValues.PreviousConversationalState
    if (prev) {
      setVariable('ConversationalState', prev)
      devLog(`[Blueprint] RestorePreviousState → ConversationalState="${prev}"`)
    } else {
      devLog('[Blueprint] RestorePreviousState: no previous value, falling back to idle')
      setVariable('ConversationalState', 'idle')
    }
  }

  if (node.type === 'sendTextInput' || node.type === 'sendVoiceTextMessage') {
    const wiredText = _resolveDataPin('in-text', node.id, nodes, edges)
    const text = wiredText !== null ? String(wiredText) : (node.data.text ?? '')
    devLog(`[EventRuntime] sendTextInput node fired — text="${text}", _sendTextInput callback set:`, !!_sendTextInput)
    if (text) _sendTextInput?.(text)
    else console.warn('[EventRuntime] sendTextInput: text was empty, skipping send')
  }

  if (node.type === 'setVariable') {
    const varName = node.data?.varName ?? ''
    const wiredValue = _resolveDataPin('in-value', node.id, nodes, edges)
    const value = wiredValue !== null ? wiredValue : (node.data?.varValue ?? '')
    if (varName) {
      setVariable(varName, value)
      devLog(`[Blueprint] setVariable("${varName}", ${JSON.stringify(value)})`)
    }
  }

  if (node.type === 'print' || node.type === 'printString') {
    // Priority: wired in-message pin → typed text → walk exec chain for event payload
    let value = _resolveDataPin('in-message', node.id, nodes, edges)
    if (value === null && node.data?.text) {
      value = node.data.text
    }
    if (value === null) {
      value = _walkUpEventData(node.id, nodes, edges)
    }
    if (value == null) value = '(no data)'

    // Print is user-authored (blueprint node) and meant to be visible.
    const _printVal = typeof value === 'object' && value !== null ? JSON.stringify(value) : value
    devLog('[Blueprint] Print:', _printVal)

    // Viewport overlay: a payload that names its own `message` shows that
    // instead of its raw JSON (printDisplayText.js has the rule and the
    // reasoning). devLog above keeps the whole object, so `code` stays
    // diagnosable in the console rather than disappearing.
    const displayText = printDisplayText(value)
    _messages.push({
      id:        _msgId++,
      text:      displayText,
      color:     node.data.color    ?? '#ffff00',
      expiresAt: Date.now() + (node.data.duration ?? 5) * 1000,
    })
  }

  if (node.type === 'headTrackedCamera') {
    // ENG-981 rework: headTrackedCamera is now an ordinary exec node — it does
    // nothing while unwired (no presence-based sync anywhere else), and every
    // time its exec chain runs it (re)syncs the runtime singleton from its
    // current `data` (enabled + all the mapping fields). Wire it from
    // onConstruct for "armed at load and live-tunable via every editor edit"
    // (mirrors the old declarative behaviour, now explicit); from beginPlay
    // for "armed only once play starts"; or from anywhere else entirely, same
    // as any other camera config node. Deleting the node from the graph, OR
    // unwiring it while leaving it present, is handled separately by
    // App.jsx's teardown watcher (mirrors the voxyConfig watcher above,
    // but checks REACHABILITY via execReachability.js, not mere presence —
    // see that watcher's comment for why presence alone missed the unwired
    // case), not by this exec case.
    devLog(`[Blueprint] headTrackedCamera: syncFromGraph enabled=${!!node.data?.enabled}`)
    headTrackedCamera.syncFromGraph(node)
    // fall through to the normal exec-out walk at the bottom of _runNode
  }

  if (node.type === 'voxyConfig') {
    // Build the threshold set, dropping any nulls so VoxyCore uses its own
    // defaults rather than NaN-ing out on an unset field.
    const fields = {
      vadThreshold:     node.data?.vadThreshold,
      vadEndThreshold:  node.data?.vadEndThreshold,
      silenceTimeoutMs: node.data?.silenceTimeoutMs,
      minSpeechMs:      node.data?.minSpeechMs,
    }
    const config = {}
    for (const k in fields) if (fields[k] != null && !Number.isNaN(Number(fields[k]))) config[k] = Number(fields[k])

    if (!voxyManager.isActive()) {
      const muted = !!node.data?.startMuted
      devLog(`[Blueprint] voxyConfig: init + start with ${JSON.stringify(config)} muted=${muted}`)
      voxyManager.init(config)
      voxyManager.start({ muted }).catch(err => console.warn('[Blueprint] voxyConfig start failed:', err))
    } else {
      devLog(`[Blueprint] voxyConfig: live-update ${JSON.stringify(config)}`)
      voxyManager.updateVadConfig(config)
    }
    // fall through to the normal exec-out walk at the bottom of _runNode
  }

  if (node.type === 'convorcherSession') {
    // Declare the convorcher session from the graph. Character/features can be
    // wired (data pins) or set inline on the node. The actual WS (re)connect is
    // owned by App.jsx via the injected callback so this module stays free of
    // the network client.
    const wiredCharacter = _resolveDataPin('in-character', node.id, nodes, edges)
    const wiredFeatures  = _resolveDataPin('in-features',  node.id, nodes, edges)
    const character = wiredCharacter !== null ? String(wiredCharacter) : (node.data?.character ?? '')
    const features  = wiredFeatures  !== null ? String(wiredFeatures)  : (node.data?.features ?? '')
    const story     = node.data?.story ?? ''
    // TTS fast-model switchover (ENG-945). '' = not set on the node, so the
    // server default applies; '0' means never use the fast model and must
    // survive as a real value rather than being folded into "unset".
    const fastSentences = node.data?.fastSentences ?? ''

    if (!character) {
      console.warn('[Blueprint] convorcherSession: no character set — skipping')
    } else if (typeof _applyConvorcherSession === 'function') {
      devLog(`[Blueprint] convorcherSession: character=${character} features=${features || '(none)'} fastSentences=${fastSentences === '' ? '(default)' : fastSentences}`)
      _applyConvorcherSession({ character, features, story, fastSentences })
    } else {
      console.warn('[Blueprint] convorcherSession: no apply callback wired (App not mounted?)')
    }
    // fall through to the normal exec-out walk at the bottom of _runNode
  }

  if (node.type === 'parlaySTT') {
    const wiredEndpoint = _resolveDataPin('in-endpoint', node.id, nodes, edges)
    const wiredKey      = _resolveDataPin('in-apiKey',   node.id, nodes, edges)
    const endpoint = wiredEndpoint !== null ? String(wiredEndpoint) : (node.data?.endpoint ?? '')
    const apiKey   = wiredKey      !== null ? String(wiredKey)      : (node.data?.apiKey   ?? '')
    const next = edges.find(e => e.source === node.id && (e.sourceHandle === 'execOut' || e.sourceHandle == null))
    const capturedEpoch = _execEpoch

    if (!endpoint || !apiKey) {
      console.warn('[Blueprint] parlaySTT: missing endpoint or apiKey — not running')
      _messages.push({ id: _msgId++, text: 'Parlay STT: missing endpoint/apiKey', color: '#d06060', expiresAt: Date.now() + 4000 })
      return  // no fallback; abort chain
    }

    const pcm = voxyManager.getLatestPCM()
    if (!pcm || pcm.byteLength === 0) {
      console.warn('[Blueprint] parlaySTT: no PCM available — voxy never delivered an utterance')
      return
    }

    const audio = _pcmToBase64(pcm)
    const t0 = performance.now()
    devLog(`[parlaySTT] → ${endpoint} pcm=${pcm.byteLength}B base64=${audio.length}B`)

    let settled = false
    const ws = new WebSocket(endpoint)
    // Expose for dev-console inspection while debugging the connection.
    if (typeof window !== 'undefined') window.__PARLAY_WS__ = ws

    const fail = (why) => {
      if (settled) return
      settled = true
      console.warn(`[parlaySTT] FAIL: ${why}`)
      _parlayTranscripts.set(node.id, '')
      _messages.push({ id: _msgId++, text: `Parlay STT: ${why}`, color: '#d06060', expiresAt: Date.now() + 4000 })
      try { ws.close() } catch { /* ignore */ }
      // Do not walk execOut — user asked for no fallback on error.
    }

    ws.onopen = () => {
      devLog(`[parlaySTT] onopen  (readyState=${ws.readyState}) t+${(performance.now()-t0).toFixed(0)}ms`)
      const payload = JSON.stringify({
        api_key:      apiKey,
        audio,
        sample_rate:  16000,
        channels:     1,
      })
      ws.send(payload)
      devLog(`[parlaySTT] sent ${payload.length}B  (bufferedAmount=${ws.bufferedAmount})`)
    }
    ws.onmessage = (ev) => {
      devLog(`[parlaySTT] onmessage t+${(performance.now()-t0).toFixed(0)}ms raw=${String(ev.data).slice(0,200)}`)
      if (settled) return
      let msg
      try { msg = JSON.parse(ev.data) } catch { return fail('invalid response JSON') }
      // Parlay sends two messages per request:
      //   1) status ack: {text: "Transcription: starting"} — no provider, no duration, no error
      //   2) final:      {text, provider, duration_ms, ...} OR {error, ...}
      // We settle on anything that carries `error`, `provider`, or `duration_ms`.
      // Other messages (i.e. the status ack) are ignored and the WS stays open.
      const isFinal = !!(msg.error || msg.provider || msg.duration_ms)
      if (!isFinal) {
        devLog(`[parlaySTT] status (keep open): ${msg.text ?? ''}`)
        return
      }
      if (msg.error) return fail(`server error: ${msg.error}`)
      settled = true
      const text = String(msg.text ?? '')
      _parlayTranscripts.set(node.id, text)
      try { ws.close() } catch { /* ignore */ }
      if (_execEpoch !== capturedEpoch) return  // interrupted by stopSpeech
      devLog(`[parlaySTT] ← "${text}" (${msg.provider ?? '?'}, ${msg.duration_ms ?? '?'}ms)`)
      _messages.push({ id: _msgId++, text: `🎤 "${text}"`, color: '#80d0ff', expiresAt: Date.now() + 4000 })
      if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
    }
    ws.onerror = (ev) => {
      console.warn(`[parlaySTT] onerror t+${(performance.now()-t0).toFixed(0)}ms readyState=${ws.readyState}`, ev)
      fail('WebSocket error')
    }
    ws.onclose = (ev) => {
      console.warn(`[parlaySTT] onclose t+${(performance.now()-t0).toFixed(0)}ms code=${ev.code} wasClean=${ev.wasClean} reason="${ev.reason}" settled=${settled}`)
      if (!settled) fail(`closed before response (code ${ev.code}${ev.reason ? `, "${ev.reason}"` : ''})`)
    }
    setTimeout(() => { if (!settled) fail('timeout (15s)') }, 15_000)
    return  // break sync chain — onmessage resumes it
  }

  if (node.type === 'playAudio') {
    const wiredUrl = _resolveDataPin('in-url', node.id, nodes, edges)
    // Precedence: wired in-url > data.audioAssetId (content-browser row, URL
    // read live off the server row) > legacy data.url (bundled path). See
    // resolveAudioAssetUrl.
    const { url, missingId } = resolveAudioAssetUrl({
      wiredUrl,
      audioAssetId: node.data?.audioAssetId ?? null,
      url: node.data?.url ?? null,
      audioAssets: CustomAssetManager.listAudioAssets(),
    })
    const next = edges.find(e => e.source === node.id && (e.sourceHandle === 'execOut' || e.sourceHandle == null))

    if (missingId) {
      // The node points at an audio asset that isn't loaded (deleted, or a
      // different character's). Warn loudly rather than silently playing
      // nothing, then continue the exec chain so the graph doesn't wedge.
      console.warn(`[Blueprint] playAudio: audio asset "${missingId}" not found for this character — nothing to play`)
    }
    if (!url) {
      if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
      return
    }

    const a = new Audio(url)
    if (typeof window !== 'undefined') {
      // Stop any in-flight audio. `src=''` cancels a pending metadata load so
      // the prior element's loadedmetadata handler won't fire and play-over
      // the new audio. `pause()` alone isn't enough — once a browser has
      // started buffering, loadedmetadata fires regardless of pause state.
      const prev = window.__PREFAB_AUDIO__
      if (prev && prev !== a) {
        try { prev.pause(); prev.src = '' } catch { /* ignore */ }
      }
      window.__PREFAB_AUDIO__ = a
    }

    // Fire-and-forget: but DELAY advancing the exec chain until the audio
    // element has reported `loadedmetadata`, so downstream nodes can read
    // this node's `out-duration` via _resolveDataPin and get a real value.
    // `_audioDurations` caches URL → duration so a second play of the same
    // URL resolves synchronously without re-waiting for metadata.
    let advanced = false
    const capturedEpoch = _execEpoch
    const advance = () => {
      if (advanced) return
      if (_execEpoch !== capturedEpoch) return  // chain was interrupted by stopSpeech
      advanced = true
      if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
    }

    // Guard against a later playAudio superseding us while we're waiting on
    // metadata: only call .play() if we're still the active prefab audio.
    const playIfCurrent = () => {
      if (typeof window !== 'undefined' && window.__PREFAB_AUDIO__ !== a) return
      a.play().catch(err => console.warn(`[Blueprint] playAudio "${url}" failed:`, err))
    }

    const cached = _audioDurations.get(url)
    if (cached !== undefined) {
      // Already know the duration — play and advance immediately.
      playIfCurrent()
      devLog(`[Blueprint] playAudio: ${url} (cached ${cached.toFixed(2)}s)`)
      advance()
    } else {
      a.addEventListener('loadedmetadata', () => {
        if (!isNaN(a.duration) && isFinite(a.duration)) {
          _audioDurations.set(url, a.duration)
        }
        playIfCurrent()
        devLog(`[Blueprint] playAudio: ${url} (${isFinite(a.duration) ? a.duration.toFixed(2) + 's' : '?'})`)
        advance()
      }, { once: true })
      a.addEventListener('error', () => {
        console.warn(`[Blueprint] playAudio "${url}" failed to load`)
        advance()
      }, { once: true })
      // Safety net — don't hang the chain forever if metadata never loads.
      setTimeout(advance, 5_000)
    }
    return  // advance() resumes the chain once metadata is ready
  }

  // ── TArray exec nodes ──
  if (node.type === 'arraySet') {
    const varName = node.data?.varName
    if (varName) {
      const wiredIdx = _resolveDataPin('in-index', node.id, nodes, edges)
      const wiredVal = _resolveDataPin('in-value',  node.id, nodes, edges)
      const index = wiredIdx !== null ? Math.round(Number(wiredIdx)) : (node.data?.index ?? 0)
      const value = wiredVal !== null ? wiredVal : (node.data?.value ?? null)
      const arr = Array.isArray(variableValues[varName]) ? [...variableValues[varName]] : []
      arr[index] = value
      setVariable(varName, arr)
      devLog(`[Blueprint] arraySet("${varName}", [${index}], ${JSON.stringify(value)})`)
    }
  }

  if (node.type === 'arrayAdd') {
    const varName = node.data?.varName
    if (varName) {
      const wiredVal = _resolveDataPin('in-value', node.id, nodes, edges)
      const value = wiredVal !== null ? wiredVal : (node.data?.value ?? null)
      const arr = Array.isArray(variableValues[varName]) ? [...variableValues[varName]] : []
      arr.push(value)
      setVariable(varName, arr)
      devLog(`[Blueprint] arrayAdd("${varName}", ${JSON.stringify(value)}) → len=${arr.length}`)
    }
  }

  if (node.type === 'arrayRemove') {
    const varName = node.data?.varName
    if (varName && Array.isArray(variableValues[varName])) {
      const wiredIdx = _resolveDataPin('in-index', node.id, nodes, edges)
      const index = wiredIdx !== null ? Math.round(Number(wiredIdx)) : (node.data?.index ?? 0)
      const arr = [...variableValues[varName]]
      arr.splice(index, 1)
      setVariable(varName, arr)
      devLog(`[Blueprint] arrayRemove("${varName}", [${index}]) → len=${arr.length}`)
    }
  }

  if (node.type === 'arrayClear') {
    const varName = node.data?.varName
    if (varName) {
      setVariable(varName, [])
      devLog(`[Blueprint] arrayClear("${varName}")`)
    }
  }

  // ── TMap exec nodes ──
  if (node.type === 'mapSet') {
    const varName = node.data?.varName
    if (varName) {
      const wiredKey = _resolveDataPin('in-key',   node.id, nodes, edges)
      const wiredVal = _resolveDataPin('in-value', node.id, nodes, edges)
      const rawKey = wiredKey !== null ? wiredKey : (node.data?.key ?? '')
      const key = node.data?.keyType === 'int' ? String(Math.round(Number(rawKey))) : String(rawKey)
      const value = wiredVal !== null ? wiredVal : (node.data?.value ?? null)
      if (key !== '') {
        const map = (variableValues[varName] && typeof variableValues[varName] === 'object' && !Array.isArray(variableValues[varName]))
          ? { ...variableValues[varName] }
          : {}
        map[key] = value
        setVariable(varName, map)
        devLog(`[Blueprint] mapSet("${varName}", "${key}", ${JSON.stringify(value)})`)
      }
    }
  }

  if (node.type === 'mapRemove') {
    const varName = node.data?.varName
    if (varName) {
      const wiredKey = _resolveDataPin('in-key', node.id, nodes, edges)
      const rawKey = wiredKey !== null ? wiredKey : (node.data?.key ?? '')
      const key = node.data?.keyType === 'int' ? String(Math.round(Number(rawKey))) : String(rawKey)
      if (key !== '') {
        const map = (variableValues[varName] && typeof variableValues[varName] === 'object' && !Array.isArray(variableValues[varName]))
          ? { ...variableValues[varName] }
          : {}
        delete map[key]
        setVariable(varName, map)
        devLog(`[Blueprint] mapRemove("${varName}", "${key}")`)
      }
    }
  }

  if (node.type === 'playBodyClip') {
    const wiredName = _resolveDataPin('in-name', node.id, nodes, edges)
    const clipName  = wiredName !== null ? String(wiredName) : (node.data?.clipName ?? '')
    const nonBlocking = node.data?.nonBlocking === true
    const next = edges.find(e => e.source === node.id && (e.sourceHandle === 'execOut' || e.sourceHandle == null))
    const capturedEpoch = _execEpoch
    const advance = () => { if (next && _execEpoch === capturedEpoch) _runFrom(next.target, nodes, edges, next.targetHandle) }

    // Start the clip: take exclusive control via __DIRECT_PLAY__ (read every
    // frame by applyWeights). Returns the scaled duration, or false if the exec
    // chain was interrupted (stopSpeech) before this ran.
    const startPlay = (action) => {
      if (_execEpoch !== capturedEpoch) return false
      const wiredSpeed = _resolveDataPin('in-speed', node.id, nodes, edges)
      const rawSpeed = wiredSpeed !== null ? Number(wiredSpeed) : Number(node.data?.playSpeed)
      const playSpeed = Number.isFinite(rawSpeed) && rawSpeed > 0 ? rawSpeed : 1.0
      const scaledDuration = action.getClip().duration / playSpeed
      action.setLoop(2200 /* THREE.LoopOnce */, 1)
      action.clampWhenFinished = true
      action.reset()
      action.timeScale = playSpeed
      action.play()
      window.__DIRECT_PLAY__ = { clipName, expiresAt: Date.now() + scaledDuration * 1000 }
      devLog(`[Blueprint] playBodyClip: "${clipName}" (${scaledDuration.toFixed(2)}s @ ${playSpeed}x${nonBlocking ? ', non-blocking' : ''})`)
      return scaledDuration
    }
    // Blocking mode resumes the chain after the clip finishes; __DIRECT_PLAY__
    // self-clears so the SM regains control.
    const scheduleBlockingResume = (scaledDuration) => {
      if (!next) return
      setTimeout(() => {
        if (_execEpoch !== capturedEpoch) return  // interrupted by stopSpeech
        window.__DIRECT_PLAY__ = null
        _runFrom(next.target, nodes, edges, next.targetHandle)
      }, scaledDuration * 1000)
    }

    const action = (typeof window !== 'undefined' ? window.__AVATAR_ACTIONS__ : null)?.[clipName]
    if (action) {
      const dur = startPlay(action)
      if (!nonBlocking) { if (dur !== false) scheduleBlockingResume(dur); return }
      // Non-blocking: advance immediately so parallel nodes (playAudio) fire too.
      advance()
      return
    }

    // JIT: clip not registered yet. Pull it to the front of the load queue and
    // play once it lands (gestures/prefab clips aren't in the priority set).
    const loader = getBodyClipLoader()
    if (loader && loader.isManaged(clipName)) {
      loader.request(clipName)
      loader.ensure(clipName).then(() => {
        const a = window.__AVATAR_ACTIONS__?.[clipName]
        if (a) {
          const dur = startPlay(a)
          if (dur !== false && !nonBlocking) scheduleBlockingResume(dur)
        } else if (!nonBlocking) {
          advance() // load failed — don't strand the blocking chain
        }
      })
      if (nonBlocking) advance() // continue the chain now; the clip joins late
      return
    }

    console.warn(`[Blueprint] playBodyClip: clip "${clipName}" not found`)
    advance() // genuinely missing — keep the exec chain moving
    return
  }

  if (node.type === 'playPrefab') {
    const wiredTag = _resolveDataPin('in-tag', node.id, nodes, edges)
    const tag      = wiredTag !== null ? String(wiredTag) : (node.data?.tag ?? '')
    const nonBlocking = node.data?.nonBlocking === true
    const next = edges.find(e => e.source === node.id && (e.sourceHandle === 'execOut' || e.sourceHandle == null))

    const all = CustomAssetManager.listPrefabs()
    devLog(`[playPrefab node] tag="${tag}" available=[${all.map(p => p.tag).join(', ') || '(none)'}]`)
    const prefab = all.find(p => p.tag === tag)
    if (!prefab) {
      console.warn(`[playPrefab node] no prefab with tag "${tag}"`)
      if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
      return
    }

    const duration = _doPlayPrefab(prefab)
    devLog(`[Blueprint] playPrefab "${tag}" | dur=${duration.toFixed(2)}s${nonBlocking ? ' (non-blocking)' : ''}`)

    if (!nonBlocking) {
      const capturedEpoch = _execEpoch
      if (next) setTimeout(() => {
        if (_execEpoch !== capturedEpoch) return
        if (typeof window !== 'undefined') window.__PREFAB_ACTIVE__ = null
        _runFrom(next.target, nodes, edges, next.targetHandle)
      }, duration * 1000)
      return
    }
    if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
    return
  }

  // ── Function call / return ──
  if (node.type === 'functionCall') {
    const fnDef = eventGraphState.functions?.[node.data?.functionId]
    if (!fnDef) {
      console.warn(`[Blueprint] functionCall: function "${node.data?.functionId}" not found`)
      const next = edges.find(e => e.source === node.id && e.sourceHandle === 'execOut')
      if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
      return
    }
    // Resolve caller-side input values
    const inputValues = {}
    for (const input of fnDef.inputs ?? []) {
      inputValues[input.name] = _resolveDataPin(`in-${input.name}`, node.id, nodes, edges)
    }
    // Push frame — callerNodeId lets functionReturn resume the chain
    _callStack.push({
      functionId:   fnDef.id,
      inputValues,
      callerNodes:  nodes,
      callerEdges:  edges,
      callerNodeId: node.id,
    })
    devLog(`[Blueprint] → call "${fnDef.name}" inputs=${JSON.stringify(inputValues)}`)
    // Run from entry; if the function is synchronous this returns after functionReturn fires
    _runFrom('entry', fnDef.nodes, fnDef.edges)
    return
  }

  if (node.type === 'functionReturn') {
    const frame = _callStack.pop()
    if (!frame) {
      console.warn('[Blueprint] functionReturn: no caller frame on stack')
      return
    }
    const fnDef = eventGraphState.functions?.[frame.functionId]
    // Resolve output values inside the function's context
    const returnValues = {}
    for (const output of fnDef?.outputs ?? []) {
      returnValues[output.name] = _resolveDataPin(`in-${output.name}`, node.id, nodes, edges)
    }
    _fnReturnValues[frame.callerNodeId] = returnValues
    devLog(`[Blueprint] ← return "${fnDef?.name}" outputs=${JSON.stringify(returnValues)}`)
    // Resume caller exec from the functionCall node's execOut
    const callerNext = frame.callerEdges.find(e =>
      e.source === frame.callerNodeId && e.sourceHandle === 'execOut'
    )
    if (callerNext) _runFrom(callerNext.target, frame.callerNodes, frame.callerEdges, callerNext.targetHandle)
    return
  }

  // Follow exec out — match by handle id or fallback to any outgoing edge
  const next = edges.find(e =>
    e.source === node.id &&
    (e.sourceHandle === 'execOut' || e.sourceHandle == null)
  )
  if (next) _runFrom(next.target, nodes, edges, next.targetHandle)
}

// Large PCM buffers blow String.fromCharCode's arg limit if passed in one
// shot — chunk through 32 KB at a time before btoa.
function _pcmToBase64(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}
